import { useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toFieldErrors } from '@/shared/api/submit';
import { SECTION_STATUS, setSectionStatus, type SectionName } from './api';

const REJECT_REASON_REQUIRED = 'Необходимо добавить причину отклонения!';

type Props = {
  requestId: number;
  section: SectionName;
  status: number;
  initialRejectMessage?: string;
  /** Block "Проверено", e.g. while the section has unsaved changes. */
  disableVerify?: boolean;
  /** Extra buttons rendered before the review buttons (e.g. "Сохранить"). */
  actions?: ReactNode;
  /** Extra feedback rendered above the buttons (e.g. save result). */
  feedback?: ReactNode;
};

/** Reject message + "Проверено" / "Отклонить" controls; goes back to the previous page on success. */
export const ReviewPanel = ({
  requestId,
  section,
  status,
  initialRejectMessage = '',
  disableVerify,
  actions,
  feedback,
}: Props) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [rejectMessage, setRejectMessage] = useState(initialRejectMessage);
  const [errors, setErrors] = useState<string[]>([]);

  const review = useMutation({
    mutationFn: (statusId: number) =>
      setSectionStatus({ requestId, section, statusId, rejectMessage }),
    onMutate: () => setErrors([]),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['verification'] });
      if (location.key === 'default') navigate('/verification');
      else navigate(-1);
    },
    onError: (error) => setErrors(Object.values(toFieldErrors(error)).flat()),
  });

  const onReject = () => {
    if (!rejectMessage.trim()) {
      setErrors([REJECT_REASON_REQUIRED]);
      return;
    }
    review.mutate(SECTION_STATUS.REJECT);
  };

  return (
    <>
      <textarea
        className="form-control"
        rows={6}
        style={{ resize: 'none' }}
        placeholder="В случае отклонения запроса на верификацию, необходимо указать причину"
        value={rejectMessage}
        onChange={(e) => setRejectMessage(e.target.value)}
      />
      {errors.map((message) => (
        <p key={message} className="text-red" style={{ marginTop: 10 }}>
          {message}
        </p>
      ))}
      {feedback}
      <div style={{ margin: '24px 0' }}>
        {actions}
        {status === SECTION_STATUS.WAITING && (
          <button
            type="button"
            className="btn btn-success"
            style={{ marginRight: 12 }}
            disabled={disableVerify || review.isPending}
            onClick={() => review.mutate(SECTION_STATUS.VERIFY)}
          >
            Проверено
          </button>
        )}
        {status !== SECTION_STATUS.REJECT && (
          <button type="button" className="btn btn-warning" disabled={review.isPending} onClick={onReject}>
            Отклонить
          </button>
        )}
      </div>
    </>
  );
};
